import { clsx } from "clsx";
import { forwardRef, type ReactNode } from "react";

interface SectionProps {
  children: ReactNode;
  className?: string;
  id?: string;
  size?: "sm" | "md" | "lg";
  noPaddingTop?: boolean;
  noPaddingBottom?: boolean;
}

/**
 * Section wrapper providing consistent vertical spacing.
 *
 * Sizes (mobile / tablet / desktop):
 * - sm: 32px / 40px / 48px (compact: social proof, dividers)
 * - md: 48px / 64px / 80px (default for content sections)
 * - lg: 64px / 96px / 128px (hero sections)
 */
export const Section = forwardRef<HTMLElement, SectionProps>(function Section(
  { children, className, id, size = "md", noPaddingTop = false, noPaddingBottom = false },
  ref,
) {
  const paddingClasses = {
    sm: {
      top: "pt-8 md:pt-10 xl:pt-12",
      bottom: "pb-8 md:pb-10 xl:pb-12",
    },
    md: {
      top: "pt-12 md:pt-16 xl:pt-20",
      bottom: "pb-12 md:pb-16 xl:pb-20",
    },
    lg: {
      top: "pt-16 md:pt-24 xl:pt-32",
      bottom: "pb-16 md:pb-24 xl:pb-32",
    },
  };

  return (
    <section
      ref={ref}
      id={id}
      style={id ? { scrollMarginTop: "var(--navbar-height)" } : undefined}
      className={clsx(
        "w-full",
        !noPaddingTop && paddingClasses[size].top,
        !noPaddingBottom && paddingClasses[size].bottom,
        className,
      )}
    >
      {children}
    </section>
  );
});
